//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Joyride, ACTIONS, EVENTS, STATUS, EventData, TooltipRenderProps
} from 'react-joyride';
import { useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';
import DeveloperError from '@/components/Elements/DeveloperError';
import {
  canStartItself, getTourInfo, validateTour
} from '@/components/Help/tours/tourSteps';
import { restoreTourFocus } from '@/components/Help/tours/tourFocus';
import { useHelp } from '@/hooks/useHelp';
import { useAuth } from '@/hooks/useAuth';
import { TourStep } from '@/components/Help/tourTypes';
import { CustomTooltip } from './CustomJoyride';
import {
  clickAdvancesStep, isTargetMissing, landingStep
} from './stepNavigation';

// How long Joyride waits for a step's target to mount before giving up.  Kept
// generous because a step's destination page can be lazy-loaded.
const TARGET_WAIT_TIMEOUT_MS = 10000;

// How often a step that said it may be absent is checked for having gone.
const ABSENT_TARGET_POLL_MS = 250;

// Resolve a step's target to an element.  Our targets are CSS selectors, but
// the type also allows an element, so guard the querySelector instead of
// casting (querySelector on a non-string throws).
const queryStepTarget = (target: TourStep['target']): Element | null => {
  if (typeof target === 'string')
    return document.querySelector(target);
  if (target instanceof HTMLElement)
    return target;
  return null;
};

const TourController = () => {
  const theme = useTheme();

  const { tourActions, tourState, tourFocus } = useHelp();
  const { isAuthenticated } = useAuth();
  const { endTour, setTourStep } = tourActions;

  const [steps, setSteps] = useState<TourStep[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const currentLoc = useLocation();
  const prevPathRef = useRef<string>(currentLoc.pathname);
  const beforeStepCleanupRef = useRef<(() => void) | null>(null);
  // Aborts the current step's advanceOnClick listener when the step is left.
  const advanceClickAbortRef = useRef<AbortController | null>(null);
  // The step Joyride last began and has not yet been left, so a departure it
  // does not report can still be settled.
  const enteredStepRef = useRef<{ index: number; step: TourStep } | null>(null);
  // The showing tooltip's answer for leaving, which the Escape listener
  // calls in place of ending the tour outright.
  const tooltipLeaveRef = useRef<(() => void) | null>(null);
  // The target of the step that was showing, kept past the end of the tour
  // for the exit focus chain to aim at. Not enteredStepRef, which is
  // cleared as its step is left -- by the time the tour has ended there is
  // no entered step, and this is asking where the user last was.
  const exitTargetRef = useRef<TourStep['target'] | null>(null);
  // Whether a tour was running as of the last commit, so the end of one can
  // be told from every other reason this component renders.
  const wasRunningRef = useRef(false);
  // The steps as of the latest commit, for listeners that outlive the render
  // that attached them: a branch swap replaces the array under them.
  const stepsRef = useRef<TourStep[]>([]);
  useEffect(() => { stepsRef.current = steps; }, [steps]);

  // Replace the current steps for the given branch name with the new steps.
  const replaceStepSequence = (
    branchName: string, newBranchSteps: TourStep[]
  ) => {
    // Add the branch name to the steps.  The name is used to find the steps for
    // the branch (the findIndex calls below).  It is expected that there is a
    // placeholder step in the main tour step definition that has
    //   branch: 'branchName'.
    const processedSteps = newBranchSteps.map(
      (step) => ({ ...step, branch: branchName }));

    // Use the functional updater so the splice runs against the latest steps,
    // not a snapshot captured when this function was created — it can be
    // invoked from a setTimeout, a MutationObserver, or an event handler.
    setSteps((prev) => {
      const startIndex = prev.findIndex((step) => step.branch === branchName);
      const endIndex = prev.findLastIndex((step) => step.branch === branchName);

      if (startIndex === -1 || endIndex === -1) {
        console.warn("Couldn't find branch.");
        return prev;
      }

      return [
        ...prev.slice(0, startIndex),
        ...processedSteps,
        ...prev.slice(endIndex + 1)
      ];
    });
  };

  // Swap in every branch's steps for the UI selection the tour starts with.
  // A selection the user changes later is picked up by the branch's own
  // beforeStep, which calls replaceStepSequence directly.
  const seedBranchSteps = () => {
    const tourInfo = getTourInfo(tourState.activeTour ?? '', isAuthenticated());
    if (!tourInfo?.branches)
      return;

    for (const [name, info] of Object.entries(tourInfo.branches)) {
      const branchSteps = info[info.currentBranch];
      if (Array.isArray(branchSteps))
        replaceStepSequence(name, branchSteps);
    }
  };

  useEffect(() => {
    // Don't do anything if the location didn't change, i.e. another dependency
    // changed.
    if (prevPathRef.current === currentLoc.pathname)
      return;

    prevPathRef.current = currentLoc.pathname;

    // Do nothing if a tour is not active.
    if (steps.length <= 0)
      return;

    if (!steps[tourState.tourStep]?.nextPath ||
        currentLoc.pathname !== steps[tourState.tourStep]?.nextPath) {
      // Somehow the user changed pages when the tour wasn't expecting it.  End
      // the tour.  One example of this is if during the login tour the user
      // hits enter in the password field to login.  This may not be the best
      // behavior for all cases, but at least it is reasonable for unexpected
      // behavior.
      endTour();
      return;
    }

    // The page changed to the expected nextPath.  Advance to the next step and
    // let Joyride wait for its target to mount, since the destination page is
    // lazy-loaded.  Joyride keeps the overlay up while waiting, so the tour
    // doesn't flicker during the transition.
    setTourStep(prev => prev + 1);
  }, [currentLoc.pathname, steps, endTour, setTourStep, tourState.tourStep]);

  useEffect(() => {
    // This should be run only when the activeTour is changed.
    try {
      if (!tourState.activeTour)
        return;

      const tourInfo = getTourInfo(tourState.activeTour, isAuthenticated());
      if (!tourInfo?.steps || tourInfo.steps.length === 0) {
        console.warn(`No steps found for tour: ${tourState.activeTour}`);
        endTour();
        return;
      }

      // Set unconditionally so a valid tour clears any errors left over from a
      // previously-loaded invalid tour.
      setValidationErrors(validateTour(tourInfo));

      setSteps(tourInfo.steps);
    } catch (error) {
      console.error('Error loading tour steps:', error);
      setSteps([]);
      endTour();
    }
  }, [tourState.activeTour, endTour, isAuthenticated]);

  // Tear down the current step's pending listeners when the tour ends or the
  // component unmounts.  Step-to-step transitions are handled in the callback;
  // this covers exits that don't fire STEP_AFTER (skip, endTour, unmount),
  // which would otherwise leave the advanceOnClick listener and the beforeStep
  // cleanup (input listeners, MutationObservers) dangling.
  useEffect(() => {
    return () => {
      advanceClickAbortRef.current?.abort();
      advanceClickAbortRef.current = null;

      if (beforeStepCleanupRef.current) {
        beforeStepCleanupRef.current();
        beforeStepCleanupRef.current = null;
      }
    };
  }, [tourState.activeTour]);

  // Escape leaves the tour.  Joyride's own Escape handling is disabled
  // (dismissKeyAction: false) so the key leaves instead of advancing, and
  // the listener sits on the document because a press lands on whatever has
  // focus -- on a step that advances on a click, a page element rather than
  // the tooltip.
  useEffect(() => {
    if (!tourState.activeTour)
      return;

    const handleEscape = (event: KeyboardEvent) => {
      // Not one something nearer the key has already answered: a step can
      // sit inside a menu that closes itself on Escape, and one press must
      // not also act on the tour around it.
      if (event.key !== 'Escape' || event.defaultPrevented)
        return;

      // The tooltip answers for the tour whenever it is up, so Escape and
      // its X mean the same thing, question and all wherever the X asks
      // one.  Without a tooltip -- a step still inside Joyride's wait for
      // its target -- the key ends the tour outright, asking nothing:
      // accepted, since there is no surface to put the question on.
      const leave = tooltipLeaveRef.current;
      if (leave) leave();
      else endTour();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [tourState.activeTour, endTour]);

  // Where the keyboard goes when the tour ends, by any of the routes that
  // end one. The tooltip holding it unmounts, and a browser answers the
  // removal of the focused element with the body, which starts the next Tab
  // at the top of the page.
  //
  // The step that was showing is the first choice, not the control the tour
  // began on. A tour navigates, so by the end the origin is usually on a
  // page that is no longer up, while the step's target was just scrolled
  // into view and explained -- it is both live and what the user is looking
  // at, which is where focus belongs. Joyride has unmounted by the time this
  // runs, its own focus handling with it, because a child's teardown
  // precedes the parent effects of the same commit.
  useEffect(() => {
    const running = tourState.activeTour !== null;
    if (wasRunningRef.current && !running) {
      // Only when the tour was the one holding the keyboard. A tooltip that
      // unmounts leaves the document body focused, which is the signal;
      // anything else focused is the user's and is not ours to move. Not
      // every end is a departure they chose -- Joyride waits up to ten
      // seconds for a step's target with no tooltip mounted and ends the
      // tour when that expires, and the unexpected-navigation guard ends it
      // too, both while the user is working the page.
      const holder = document.activeElement;
      const target = exitTargetRef.current;
      if (holder === null || holder === document.body)
        restoreTourFocus([
          target === null ? null : queryStepTarget(target),
          tourFocus.originRef.current,
          tourFocus.fallbackRef.current
        ]);
      // Neither outlives the tour that set it: a later tour records its own,
      // and a stale one would aim the next exit at the wrong place.
      exitTargetRef.current = null;
      tourFocus.originRef.current = null;
    }
    wasRunningRef.current = running;
  }, [tourState.activeTour, tourFocus]);

  // What every departure from a step owes it. Joyride reports a departure it
  // made itself (STEP_AFTER); one made by setting the step index -- a
  // click-to-advance step, a route change, the starter, a dot -- it does not,
  // and the step-change effect below settles those.
  const leaveStep = useCallback((step: TourStep | undefined) => {
    enteredStepRef.current = null;
    step?.afterStep?.(tourActions, tourState);
    if (beforeStepCleanupRef.current) {
      beforeStepCleanupRef.current();
      beforeStepCleanupRef.current = null;
    }
    advanceClickAbortRef.current?.abort();
    advanceClickAbortRef.current = null;
  }, [tourActions, tourState]);

  // A step still entered when the index has moved on was left without
  // Joyride saying so.
  useEffect(() => {
    const entered = enteredStepRef.current;
    if (entered && entered.index !== tourState.tourStep)
      leaveStep(entered.step);
  }, [tourState.tourStep, leaveStep]);

  // A step that said it may be absent can go while it is showing: the menu
  // it lives in closes, or the panel it is inside is parked. Joyride reports
  // a missing target only when a step's lifecycle changes, so without this
  // the tour sits over a spotlight framing nothing, with Next held shut.
  // Polled rather than observed because the ways a target can go -- removed,
  // hidden, laid out to nothing -- are not one kind of mutation.
  useEffect(() => {
    const index = tourState.tourStep;
    const step = steps[index];
    if (!tourState.activeTour || !step?.skipIfAbsent) return;
    // Not yet arrived is not gone: Joyride waits for a target of its own
    // accord, and this must not answer for that wait.
    if (isTargetMissing(step.target)) return;

    const timer = setInterval(() => {
      if (!isTargetMissing(step.target)) return;
      clearInterval(timer);
      // Back to the step before it, which is the one that brings it back.
      const fallback = landingStep(steps, index - 1, -1);
      if (fallback >= 0) setTourStep(fallback);
      else endTour();
    }, ABSENT_TARGET_POLL_MS);
    return () => clearInterval(timer);
  }, [tourState.activeTour, tourState.tourStep, steps, setTourStep, endTour]);

  const customJoyrideCallback = (data: EventData) => {
    const { action, index, status, type } = data;
    const step = data.step as TourStep;

    if (type === EVENTS.TOUR_START) {
      // Seed every branch's steps from the current UI selections.  The timeout
      // defers the splice so it doesn't run while joyride is still updating
      // internally, which otherwise left tooltips mispositioned.
      setTimeout(seedBranchSteps, 0);
    } else if (type === EVENTS.STEP_BEFORE) {
      // Should Joyride begin the next step before the step-change effect has
      // run, the step being left is settled here first.
      const entered = enteredStepRef.current;
      if (entered && entered.index !== index) leaveStep(entered.step);
      enteredStepRef.current = { index, step };
      exitTargetRef.current = step?.target ?? null;
      // Start every step with next enabled.
      tourActions.setIsNextDisabled(false);

      if (step?.advanceOnClick) {
        // This step advances on the click of a UI element.  Add a handler to
        // listen for a click on the target.  The listener is tied to an
        // AbortController so it can be removed when the step is left without
        // clicking (otherwise it would linger on persistent elements and could
        // later fire with a stale step index).
        advanceClickAbortRef.current?.abort();
        const controller = new AbortController();
        advanceClickAbortRef.current = controller;

        const targetElement = queryStepTarget(step.target);
        targetElement?.addEventListener('click', (event) => {
          // The step as the list stands now, not as it stood when the
          // listener was attached: a branch swap can replace or shorten the
          // list under a listener, and a step that is gone advances nothing.
          const current = stepsRef.current[index];
          if (!current) return;
          if (current.clickWithin &&
              !(event.target instanceof Element &&
                event.target.closest(current.clickWithin)))
            return;
          if (!clickAdvancesStep(current.nextPath, currentLoc.pathname))
            return;
          leaveStep(current);
          tourActions.setTourStep(index + 1);
        }, { signal: controller.signal });
      }

      if (step?.beforeStep) {
        // Run the beforeStep() function and save any cleanup function.
        const maybeCleanup = step.beforeStep(tourActions, tourState,
                                             replaceStepSequence);
        if (typeof maybeCleanup === 'function')
          beforeStepCleanupRef.current = maybeCleanup;
      }

      if (step?.autoFocus) {
        // Set focus if the field is a Mui TextField.  The timeout gives some
        // time for possible re-rendering to settle.  Support for other
        // Mui components could be added as well.
        const field = queryStepTarget(step.target);
        if (field?.className.includes('MuiTextField-root')) {
          const inputField = field?.querySelector('input');
          if (inputField) {
            setTimeout(() => { inputField.focus(); }, 100);
          }
        }
      }
    } else if(type === EVENTS.STEP_AFTER) {
      leaveStep(step);
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      if (step?.target === 'none' &&
          tourState.tourStep === index && index + 1 < steps.length) {
        // An unreplaced branch placeholder (replaceStepSequence swaps in the
        // real steps for the selected branch).  Skip past it rather than
        // ending the tour, in case replacement hasn't happened yet.
        tourActions.setTourStep(index + 1);
      } else if (step?.skipIfAbsent) {
        // A step that said it may be absent and is: the menu closed without
        // a choice, or the click that should have opened it did not. Back to
        // the step before it, which is what opens it.
        const fallback = landingStep(steps, index - 1, -1);
        if (fallback >= 0) tourActions.setTourStep(fallback);
        else tourActions.endTour();
      } else {
        // A real step whose target never appeared — for example, the user
        // closed the first step of the dashboard tour while on a different
        // page, which advances toward steps whose targets only exist on the
        // dashboard.  The tour can't continue, so end it cleanly instead of
        // leaving it stuck.
        tourActions.endTour();
      }
    } else if (type === EVENTS.STEP_AFTER) {
      // Advance (or step back) the tour, but only if the user didn't click on
      // the navigation dots.
      if (tourState.tourStep === index) {
        // Passing over any step that said it may be absent and is.
        const direction = action === ACTIONS.PREV ? -1 : 1;
        const nextStep = landingStep(steps, index + direction, direction);

        if (action === ACTIONS.NEXT && index === steps.length - 1) {
          // The primary button on the last step, which is the one Joyride
          // labels Finish -- the single exit that says the user is done
          // with the tour rather than merely out of it. Asked as "which
          // button, on which step" rather than "where did the index land":
          // a trailing step that said it may be absent and is lands the
          // index off the end from a plain Next.
          tourActions.finishTour();
        } else if (nextStep < 0 || nextStep >= steps.length ||
                   isTargetMissing(steps[nextStep].target)) {
          // Back past the start; a next target that only exists on another
          // page; or a plain Next from the second-to-last step that landed
          // past the end because landingStep walked it over a trailing
          // skipIfAbsent step that is gone. None of these is the user
          // saying they are done, so end cleanly rather than record an
          // opt-out or advance into Joyride's target wait, which lingers
          // the overlay before canceling.
          tourActions.endTour();
        } else {
          tourActions.setTourStep(nextStep);
        }
      }
    } else if (status === STATUS.FINISHED) {
      // Joyride's own index >= size bookkeeping, not a button the user
      // pressed -- it can fire when a branch resync shrinks the steps
      // array out from under the current index. The opt-out belongs to
      // the button that says the user is done (handled above), not to
      // this.
      tourActions.endTour();
    }
  };

  const activeTour = tourState.activeTour;

  // Leaving is worth a question only while the tour can still come back
  // uninvited, which is the starter's question too and is answered in one
  // place for both. A run the user picked from the Help menu is otherwise
  // theirs to leave, however this run of it began.
  const asksBeforeLeaving = activeTour !== null &&
    canStartItself(getTourInfo(activeTour, isAuthenticated()),
                   tourState.optedOutTours);

  // What the tooltip needs, read at render rather than closed over, so the
  // component below can keep one identity for the life of the tour.  The
  // handler ref goes the other way: the tooltip publishes into it.
  const tooltipDataRef = useRef({ steps, asksBeforeLeaving });
  tooltipDataRef.current = { steps, asksBeforeLeaving };

  // Joyride takes this as a component type, so a new identity does not
  // re-render the tooltip -- it unmounts one component and mounts another,
  // losing the tooltip's own state, which is where the exit prompt lives.
  // Splicing a branch's steps in replaces the steps array, so depending on
  // it here would drop that prompt under the user's hand.
  const tooltipComponent = useCallback((props: TooltipRenderProps) => (
    <CustomTooltip {...props} steps={tooltipDataRef.current.steps}
      asksBeforeLeaving={tooltipDataRef.current.asksBeforeLeaving}
      leaveHandlerRef={tooltipLeaveRef} />
  ), []);

  if (validationErrors.length > 0) {
    return (
      <DeveloperError title="Tour Step Validation Failed">
        <ul>
          {validationErrors.map((error, index) => {
            return (<li key={index}>{error}</li>);
          })}
        </ul>
      </DeveloperError>
    );
  }

  // Render only when there is an active tour with steps.
  return activeTour !== null && steps.length > 0 && (
    <Joyride
      run={Boolean(tourState.activeTour)}
      steps={steps}
      stepIndex={tourState.tourStep}
      continuous
      scrollToFirstStep={true}
      // Don't flash a spinner while waiting for a target; the overlay stays up
      // during the wait, which is enough feedback during a page transition.
      loaderComponent={null}
      tooltipComponent={tooltipComponent}
      onEvent={customJoyrideCallback}
      locale={{ last: 'Finish' }}
      options={{
        primaryColor: theme.colors.rocketRed.p50,
        arrowColor: theme.palette.background.paper,
        backgroundColor: theme.palette.background.paper,
        textColor: theme.palette.text.primary,
        // Just behind modal to allow select boxes to appear on top of the
        // overaly.
        zIndex: 1299,
        scrollOffset: 220,
        // Disable closing the tour when the overlay is clicked.
        overlayClickAction: false,
        // Disable Joyride's Escape handling; we end the tour on Escape instead
        // of advancing it (see the keydown effect).
        dismissKeyAction: false,
        // Joyride's beacon is a dot the user must click before a step opens,
        // and with no pause to mean it is the only thing a dot could be
        // mistaken for.  Set here rather than per step so it covers every
        // way a step enters the array, including the branch splice; a step
        // that wants a dot can still say `skipBeacon: false`, which wins
        // over this.
        skipBeacon: true,
        // Border radius of the spotlight cutout.
        spotlightRadius: 8,
        // Wait long enough for a lazy-loaded page's target to mount after a
        // cross-page step transition.
        targetWaitTimeout: TARGET_WAIT_TIMEOUT_MS,
      }}
      styles={{
        spotlight: {
          // The spotlight is an SVG path, so its border is a stroke.
          stroke: theme.colors.rocketRed.p50,
          strokeWidth: 3,
        },
      }}
    />
  );
};

export default TourController;
